import {
    DossierOpenSearch,
    NestedDossierProcessesOpenSearch,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, useForm } from 'react-hook-form';
import { OptionType } from '@europrocurement/flexy-components';
import { type DossierArticle, useDossierProcesses } from '../../hooks';

type UpdateValideurFormProps = {
    validatorTypeId: number;
    processing: boolean;
    article: DossierArticle;
    dossier: DossierOpenSearch;
    onItemSelected: () => void;
    currentStatus: OptionType;
};

const UpdateValidatorForm: React.FunctionComponent<UpdateValideurFormProps> = function ({
    validatorTypeId,
    processing,
    article,
    dossier,
    onItemSelected,
    currentStatus,
}) {
    const { validatorsApi } = useFormaliteService();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { findProcessByArticle, updateValidatorProcess, createValidatorProcess } =
        useDossierProcesses();
    const [typeStatus, setTypeStatus] = useState<OptionType[]>([]);
    const [articleProcess, setArticlesProcess] = useState<
        NestedDossierProcessesOpenSearch | undefined
    >();

    useEffect(() => {
        const processes = findProcessByArticle(dossier, [validatorTypeId], article.id);
        setArticlesProcess(processes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!articleProcess) {
            return;
        }

        validatorsApi
            .apiDossiersValideursIdGet({ id: articleProcess.validatorId.toString() })
            .then((res) => {
                const optionsTypes: OptionType[] = [];
                if (res.data.statuses) {
                    // sort validator statuses by sort order property
                    const sortedArray = res.data.statuses.sort(
                        (a, b) => (a.sortOrder as number) - (b.sortOrder as number),
                    );
                    // Extract values to generate a select
                    sortedArray.map((status) =>
                        optionsTypes.push({
                            value: status.code,
                            label: status.label as unknown as string,
                        }),
                    );
                }

                setTypeStatus(optionsTypes);
            })
            .catch((err) =>
                apiErrorMessageSnackbar(
                    err,
                    'Une erreur est survenue lors de la récupération des statuts',
                ),
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articleProcess]);

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
    });

    const selectedStatus = formContext.watch('statusCode');

    // Met à jour la valeur par défaut
    useEffect(() => {
        if (currentStatus) {
            formContext.setValue('statusCode', currentStatus);
        }
    }, [currentStatus, formContext]);

    const handleFormSubmit = async (value: string) => {
        if (value) {
            if (!articleProcess) {
                await createValidatorProcess(article.dossierId, [article.id]);
            } else {
                await updateValidatorProcess(articleProcess.id as number, value);
            }

            onItemSelected();
        }
    };

    // Catch select onChange
    useEffect(() => {
        if (!processing && selectedStatus && selectedStatus.value !== currentStatus.value) {
            handleFormSubmit(selectedStatus.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatus, currentStatus]);

    const formProps = {
        formContext,
        onSubmit: () => {},
        submitButton: {
            displayed: false,
        },
    };

    const formStructure: FormStructure[] = [
        {
            type: 'select',
            name: 'statusCode',
            inputlabel: 'Statut',
            options: typeStatus,
            getOptionLabel: ({ label }: { label: string }) => label,
            getOptionValue: ({ value }: { value: string }) => value,
            xs: 12,
        },
    ];

    return (
        <Box sx={{ minWidth: '500px' }}>
            <Typography variant="l2dh6">Modifier le statut</Typography>
            <Box
                sx={{
                    padding: '26px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Box>
        </Box>
    );
};

export default UpdateValidatorForm;
